/**
 * ManualPresenter
 * --
 */
import React from 'react';
import { Content, Row, Col, Title, Text } from 'components';
import {
  Alert,
  Avatar,
  Button,
  DatePicker,
  Descriptions,
  Flex,
  Input,
  InputNumber,
  Radio,
  Select,
  Spin,
} from 'antd';
import Formatter from 'utils/Formatter';
import { CloseOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import MessageAlert, { STORE_URL, stringToMoneyFormat } from 'utils';
import { ProductModal } from './components';
import { openAddressModal } from 'utils/DaumAPIManager';
import { PAYMENT_METHOD_CONSTANT } from 'constants/ProductConstants';
// import PaymentStatus from 'constants/PaymentStatusConstants';
import dayjs from 'dayjs';

const TYPE = [
  {
    label: '배송',
    value: 'DELIVERY',
  },
  {
    label: '스토어픽업',
    value: 'STORE_PICKUP',
  },
];

const ManualPresenter = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const {
    // auth = 'ADMIN',
    user = null,
    resetUser,
    isLoading = false,
    userPhoneNumb = '',
    setUserPhoneNumb = () => { },
    onChange = () => { },
    onSearchUser = () => { },
    type = 'DELIVERY',
    setType = () => { },
    receives = [],
    pickUp = null,
    setPickUp = () => { },
    products = [],
    selectedProduct = null,
    setSelectedProduct = () => { },
    productInformation = [],
    onProductInformation = () => { },
    onProductPrice = () => { },
    forwardingInformation = [],
    onDeliveryPriceChange = () => { },
    deliveryAddress = {},
    setDeliveryAddress = () => { },
    paymentInfo = {},
    setPaymentInfo = () => { },
    onSubmit = () => { },
  } = props;

  /* ===== STATE ===== */
  /* ===== VARIABLE ===== */
  const styles = {
    title: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 20,
    },
    subTitle: {
      fontSize: 17,
      fontWeight: 500,
      lineHeight: '141%',
      marginBottom: 20,
    },
  };

  /* ===== FUNCTION ===== */
  /**
   * 라벨 렌더
   * --
   * @param {string} label
   * @param {boolean} required
   * @returns
   */
  const renderLabel = (label = '', required = true) => {
    return (
      <Flex gap={4}>
        <p style={{ fontSize: 14, color: '#111111', wordBreak: 'keep-all' }}>
          {label}
        </p>
        {required ? (
          <span style={{ fontSize: 14, color: '#FF4242' }}>*</span>
        ) : null}
      </Flex>
    );
  };

  /**
   * ANCHOR 다음 주소 API
   * --
   */
  const handleAddressModal = () => {
    openAddressModal((res) => {
      const { address, zonecode } = res;

      onChange('object', setDeliveryAddress, zonecode, 'recipient_postcode');
      onChange('object', setDeliveryAddress, address, 'recipient_address');
    });
  };

  /* ===== HOOKS ===== */
  /**
   * ANCHOR Form
   * --
   */
  const formItem = React.useMemo(() => {
    return [
      // SECTION: 회원선택
      {
        label: renderLabel('회원 선택'),
        children: (
          <Flex vertical gap={12}>
            <Flex gap={12}>
              <Input
                size="large"
                value={userPhoneNumb}
                onChange={(e) =>
                  onChange(
                    'string',
                    setUserPhoneNumb,
                    Formatter.hyphen(e.target.value)
                  )
                }
                placeholder="휴대폰 번호를 입력해주세요."
                style={{ width: 500 }}
                disabled={!!user}
              />
              <Button
                size="large"
                onClick={() => onSearchUser()}
                disabled={!!user}
              >
                회원찾기
              </Button>
            </Flex>
            {user ? (
              <Flex gap={4} align="center">
                <Text size={14}>{user?.user_name}</Text>
                <Text size={14}>({user?.user_account})</Text>
                <Button danger size="small" onClick={() => resetUser()}>
                  <CloseOutlined />
                </Button>
              </Flex>
            ) : null}
          </Flex>
        ),
        span: 2,
      },
      // !SECTION: 회원선택

      // SECTION: 주문 타입
      {
        label: renderLabel('주문 타입'),
        children: (
          <Flex>
            <Radio.Group
              value={type}
              onChange={(e) => {
                if (
                  productInformation.length &&
                  !window.confirm(
                    '주문 타입 변경 시 선택한 상품이 초기화 됩니다. 변경하시겠습니까?'
                  )
                )
                  return;

                onProductInformation('RESET');
                setPickUp(null);
                onChange('string', setType, e.target.value);
              }}
            >
              {TYPE.map((item) => (
                <Radio value={item.value}>{item.label}</Radio>
              ))}
            </Radio.Group>
          </Flex>
        ),
      },
      // !SECTION: 주문 타입

      // SECTION: 픽업지
      {
        label: renderLabel('픽업지', false),
        children: (
          <Flex vertical>
            <Select
              size="large"
              value={pickUp?.forwarding_location_id}
              options={receives.map((receive) => ({
                ...receive,
                label: receive.forwarding_name,
                value: receive.forwarding_location_id,
              }))}
              optionRender={({ data }) => (
                <Flex vertical>
                  <Text size={14} color={'#666666'}>
                    {data.forwarding_name}
                  </Text>
                  <Text size={12} color={'#E7E7E7'}>
                    ({data.forwarding_postcode}) {data.forwarding_address}{' '}
                    {data.forwarding_address_detail}
                  </Text>
                </Flex>
              )}
              disabled={type === 'DELIVERY'}
              placeholder={'픽업지를 선택해주세요.'}
              showSearch
              optionFilterProp="label"
              onChange={(_, option) => setPickUp(option)}
            />
          </Flex>
        ),
      },
      // !SECTION: 픽업지

      // SECTION 상품 선택
      {
        label: renderLabel('상품 선택'),
        children: (
          <Flex vertical gap={12}>
            <Select
              size="large"
              style={{ width: 500 }}
              value={selectedProduct?.product_id}
              options={products.map((product) => ({
                ...product,
                label: product.product_name,
                value: product.product_id,
              }))}
              optionRender={({ data }) => (
                <Flex align="center" gap={8}>
                  <Avatar
                    src={
                      data.thumbnail
                        ? `${STORE_URL}/${data.thumbnail.file_url}`
                        : null
                    }
                    size={50}
                    shape="square"
                  />
                  <Text size={14} color={'#666666'}>
                    {data.product_name}
                  </Text>
                </Flex>
              )}
              placeholder={
                type === 'STORE_PICKUP' && !pickUp
                  ? '픽업지를 선택해주세요'
                  : '상품을 선택해주세요'
              }
              disabled={type === 'STORE_PICKUP' && !pickUp ? true : false}
              showSearch
              optionFilterProp="label"
              onChange={(_, option) => setSelectedProduct(option)}
            />

            <Flex gap={12} style={{ flexWrap: 'wrap' }}>
              {productInformation?.map((item) => (
                <Flex
                  gap={12}
                  flex={'0 0 calc(50% - 16px)'}
                  style={{ position: 'relative', top: 0, left: 0 }}
                >
                  <Avatar
                    src={`${STORE_URL}/${item?.thumbnail?.file_url}`}
                    shape="square"
                    style={{
                      width: 200,
                      height: 200 * 1.25,
                      border: '1px solid #EEEEEE',
                    }}
                  />

                  <Flex vertical style={{ padding: 12 }} gap={8}>
                    <Text size={12} color={'#BDBDBD'}>
                      {item?.shop_information?.shop_information_name}
                    </Text>
                    <Text size={18} weight={700}>
                      {item?.product_name}
                    </Text>

                    <Flex vertical>
                      {!item?.product_option
                        ? null
                        : item?.product_option?.map((op) => (
                          <Text size={12} color={'#666666'}>
                            {op?.option?.map((sub) =>
                              sub?.selected
                                ? `${op?.option_title} : ${sub?.option_name
                                } (+ ${stringToMoneyFormat(
                                  sub?.option_price,
                                  '원'
                                )})`
                                : ''
                            )}
                          </Text>
                        ))}
                    </Flex>

                    <Flex gap={4}>
                      <Text size={14}>수량: {item?.num} 개</Text>
                      <Text size={14}>/</Text>
                      <Text size={14} weight={700}>
                        총 금액[(원가 + 옵션금) x 수량]:{' '}
                        {stringToMoneyFormat(
                          (item?.price + item?.option_price) * item?.num,
                          '원'
                        )}
                      </Text>
                    </Flex>

                    <Flex align="center" gap={8}>
                      <Flex align="center" gap={2}>
                        <Text size={12} color={'#BDBDBD'}>
                          할인금
                        </Text>
                        <InputNumber
                          value={item?.discount_price ?? 0}
                          onChange={(e) =>
                            onProductPrice(
                              item?.product_key,
                              'discount_price',
                              e
                            )
                          }
                          min={0}
                          max={(item?.price + item?.option_price) * item?.num}
                          placeholder="할인금"
                          controls={false}
                          suffix={'원'}
                          formatter={(value) =>
                            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                          }
                        />
                      </Flex>

                      <Text size={12} color={'#BDBDBD'}>
                        /
                      </Text>

                      <Flex align="center" gap={2}>
                        <Text size={12} color={'#BDBDBD'}>
                          적립금
                        </Text>
                        <InputNumber
                          value={item?.reserve_price ?? 0}
                          onChange={(e) =>
                            onProductPrice(
                              item?.product_key,
                              'reserve_price',
                              e
                            )
                          }
                          min={0}
                          placeholder="적립금"
                          controls={false}
                          suffix={'원'}
                          formatter={(value) =>
                            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                          }
                        />
                      </Flex>
                    </Flex>

                    <Flex align="center" gap={8}>
                      <Flex align="center" gap={2}>
                        <Text size={12} color={'#BDBDBD'}>
                          수수료
                        </Text>

                        <InputNumber
                          value={item?.commission_rate ?? 0}
                          onChange={(e) =>
                            onProductPrice(
                              item?.product_key,
                              'commission_rate',
                              e
                            )
                          }
                          min={0}
                          max={
                            item?.commission_type === 'R'
                              ? 100
                              : (item?.price + item?.option_price) * item?.num
                          }
                          placeholder="수수료"
                          controls={false}
                          suffix={item?.commission_type === 'R' ? '%' : '원'}
                          formatter={(value) =>
                            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                          }
                        />
                        <Select
                          value={item?.commission_type}
                          options={[
                            { label: '%', value: 'R' },
                            { label: '₩', value: 'W' },
                          ]}
                          onChange={(e) =>
                            onProductPrice(
                              item?.product_key,
                              'commission_type',
                              e
                            )
                          }
                        />
                      </Flex>
                    </Flex>

                    <Flex gap={4} align="center">
                      <Text size={14}>결제금액:</Text>
                      <Text size={18} weight={700} color={'#D33444'}>
                        {stringToMoneyFormat(
                          (item?.price + item?.option_price) * item?.num -
                          item?.discount_price,
                          '원'
                        )}
                      </Text>
                    </Flex>
                  </Flex>

                  <Flex
                    style={{ position: 'absolute', top: 10, right: 10 }}
                    gap={4}
                  >
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => setSelectedProduct(item)}
                    />
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() =>
                        onProductInformation('REMOVE', item?.product_key, () =>
                          MessageAlert.success('상품목록에서 제거되었습니다.')
                        )
                      }
                    />
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </Flex>
        ),
        span: 2,
      },
      // !SECTION 상품 선택

      // SECTION: 배송지/출고지 별 상품 정보
      ...(type === 'DELIVERY'
        ? [
          // SECTION: 배송지
          {
            label: renderLabel('배송지'),
            children: (
              <Flex gap={12} vertical>
                <Flex vertical gap={4}>
                  <Text size={12} color={'#666666'}>
                    배송지 정보
                  </Text>
                  <Flex gap={4}>
                    <Input
                      value={deliveryAddress?.recipient_postcode}
                      size="large"
                      readOnly
                      placeholder="우편번호"
                    />
                    <Button size="large" onClick={() => handleAddressModal()}>
                      우편번호 찾기
                    </Button>
                  </Flex>
                  <Input
                    value={deliveryAddress?.recipient_address}
                    size="large"
                    readOnly
                    placeholder="주소"
                  />
                  <Input
                    value={deliveryAddress?.recipient_address_detail}
                    size="large"
                    placeholder="상세주소"
                    onChange={(e) =>
                      onChange(
                        'object',
                        setDeliveryAddress,
                        e.target.value,
                        'recipient_address_detail'
                      )
                    }
                  />
                </Flex>

                <Flex vertical gap={4}>
                  <Text size={12} color={'#666666'}>
                    수령인 정보
                  </Text>
                  <Input
                    size="large"
                    placeholder="수령자 명"
                    value={deliveryAddress?.recipient_name}
                    onChange={(e) =>
                      onChange(
                        'object',
                        setDeliveryAddress,
                        e.target.value,
                        'recipient_name'
                      )
                    }
                  />
                  <Input
                    size="large"
                    placeholder="수령자 연락처"
                    value={deliveryAddress?.recipient_phone}
                    onChange={(e) =>
                      onChange(
                        'object',
                        setDeliveryAddress,
                        Formatter.hyphen(e.target.value),
                        'recipient_phone'
                      )
                    }
                  />
                  <Input
                    size="large"
                    placeholder="요청사항"
                    value={deliveryAddress?.request}
                    onChange={(e) =>
                      onChange(
                        'object',
                        setDeliveryAddress,
                        e.target.value,
                        'request'
                      )
                    }
                  />
                </Flex>
              </Flex>
            ),
          },
          // !SECTION: 배송지
          // SECTION: 출고지 정보
          {
            label: renderLabel('출고지 정보', false),
            children: (
              <Flex vertical gap={12}>
                {forwardingInformation.map((item) => (
                  <Flex vertical gap={12}>
                    <Flex vertical>
                      <Text size={18} weight={700}>
                        {item?.forwarding_name}
                      </Text>

                      <Text size={14} color={'#666666'}>
                        ({item?.forwarding_postcode}){' '}
                        {item?.forwarding_address}{' '}
                        {item?.forwarding_address_detail}
                      </Text>

                      <Flex gap={4}>
                        <Text size={14}>
                          기본 배송비:{' '}
                          {stringToMoneyFormat(item?.delivery_price, '원')}
                        </Text>

                        <Text size={14}>/</Text>

                        <Text size={14}>
                          무료 배송 기준 금액:{' '}
                          {stringToMoneyFormat(
                            item?.free_delivery_price,
                            '원 이상 무료배송'
                          )}
                        </Text>
                      </Flex>
                    </Flex>

                    <Flex vertical>
                      <Text weight={600} color={'#111111'}>
                        {`${item?.product_information?.[0]?.product_name}${item?.product_information.length > 1
                          ? ` 외 ${item?.product_information?.length - 1}건`
                          : ''
                          }`}
                      </Text>

                      <Flex gap={4} align="center">
                        <Text weight={700} color={'#707070'}>
                          총 결제 금액:{' '}
                          {stringToMoneyFormat(
                            item?.total_price - item?.final_discount_price,
                            '원'
                          )}
                        </Text>

                        <Text weight={700} color={'#707070'}>
                          /
                        </Text>

                        <Flex align="center" gap={4}>
                          <Text weight={700} color={'#707070'}>
                            적용 배송비:
                          </Text>

                          <InputNumber
                            value={item?.apply_delivery_price ?? 0}
                            controls={false}
                            formatter={(value) =>
                              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                            }
                            suffix={'원'}
                            onChange={(e) =>
                              onDeliveryPriceChange(
                                item?.forwarding_location_id,
                                e
                              )
                            }
                          />
                        </Flex>
                      </Flex>
                    </Flex>
                  </Flex>
                ))}
              </Flex>
            ),
          },
          // !SECTION: 출고지 정보
        ]
        : []),
      // !SECTION: 배송지/출고지 별 상품 정보

      // SECTION: 결제 정보
      {
        label: renderLabel('결제 정보'),
        children: (
          <Flex vertical gap={12}>
            <Flex vertical gap={4}>
              <Text size={14} color={'#666666'}>
                결제수단
              </Text>

              <Radio.Group
                value={paymentInfo?.payment_method}
                onChange={(e) =>
                  onChange(
                    'object',
                    setPaymentInfo,
                    e.target.value,
                    'payment_method'
                  )
                }
              >
                {Object.entries(PAYMENT_METHOD_CONSTANT).map(([key, value]) => (
                  <Radio.Button value={key}>{value?.title}</Radio.Button>
                ))}
              </Radio.Group>
            </Flex>

            {/* <Flex vertical gap={4}>
              <Text size={14} color={'#666666'}>
                결제상태
              </Text>

              <Radio.Group
                value={paymentInfo?.payment_state}
                onChange={(e) =>
                  onChange(
                    'object',
                    setPaymentInfo,
                    e.target.value,
                    'payment_state'
                  )
                }
              >
                {Object.entries(PaymentStatus).map(([key, value]) => (
                  <Radio.Button value={key}>{value?.title}</Radio.Button>
                ))}
              </Radio.Group>
            </Flex> */}

            <Flex vertical gap={4}>
              <Text size={14} color={'#666666'}>
                주문경로
              </Text>

              <Radio.Group
                value={paymentInfo?.order_route}
                onChange={(e) =>
                  onChange(
                    'object',
                    setPaymentInfo,
                    e.target.value,
                    'order_route'
                  )
                }
              >
                <Radio.Button value={'PC'}>PC</Radio.Button>
                <Radio.Button value={'APP'}>APP</Radio.Button>
              </Radio.Group>
            </Flex>

            <Flex vertical gap={4}>
              <Text size={14} color={'#666666'}>
                주문일
              </Text>

              <DatePicker
                size="large"
                value={
                  paymentInfo?.payment_date
                    ? dayjs.unix(paymentInfo?.payment_date)
                    : ''
                }
                onChange={(_, str) =>
                  onChange(
                    'object',
                    setPaymentInfo,
                    dayjs(str).unix(),
                    'payment_date'
                  )
                }
                placeholder="주문일"
                style={{ width: 230 }}
                showTime
                formatter={'YYYY.MM.DD HH:mm:ss'}
              />
            </Flex>
          </Flex>
        ),
      },
      // !SECTION: 결제 정보
    ];
    // eslint-disable-next-line
  }, [
    userPhoneNumb,
    user,
    type,
    receives,
    pickUp,
    products,
    selectedProduct,
    productInformation,
    forwardingInformation,
    deliveryAddress,
    paymentInfo,
  ]);

  /* ===== RENDER ===== */
  return (
    <>
      <Spin spinning={isLoading}>
        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          <Row gutter={[16, 16]}>
            <Col x={24}>
              <Title style={{ ...styles.title }}>주문장 생성</Title>
            </Col>

            <Col x={24}>
              <Alert
                type="warning"
                showIcon
                message={<h4>주의!</h4>}
                description={
                  <p>
                    주문장 생성 시 매출에 반영됩니다.
                    <br />
                    주문장이 누락되었을 경우 누락된 주문건에 대해서만
                    사용해주세요.
                  </p>
                }
              />
            </Col>

            <Col x={24}>
              <Content maxWidth={'100%'} padding={20}>
                {/* SECTION: 입력 폼 */}
                <Descriptions
                  bordered
                  items={formItem.map((item, index) => ({
                    ...item,
                    key: index + 1,
                  }))}
                  column={2}
                />
                {/* !SECTION: 입력 폼 */}
              </Content>
            </Col>

            <Col x={24}>
              <Flex justify="center" align="center">
                <Button
                  size="large"
                  type="primary"
                  onClick={() => onSubmit()}
                  style={{ width: '30%', height: 54 }}
                >
                  생성
                </Button>
              </Flex>
            </Col>
          </Row>
        </Content>
      </Spin>

      <ProductModal
        data={selectedProduct}
        onSubmit={(product, callback) =>
          onProductInformation('ADD', product, callback)
        }
        onUpdate={(product, callback) =>
          onProductInformation('UPDATE', product, callback)
        }
        onClose={() => { 
          onProductInformation('CANCEL'); }}
      />
    </>
  );
};

export default ManualPresenter;
